import { type LocalUserChoices, usePersistentUserChoices, usePreviewTracks } from '@livekit/components-react';
import { Box, Button, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { useHotkeys, useLocalStorage } from '@mantine/hooks';
import { IconMicrophone, IconMicrophoneOff, IconVideo, IconVideoOff, IconVolume2 } from '@tabler/icons-react';
import type { LocalAudioTrack, LocalVideoTrack } from 'livekit-client';
import { type SubmitEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { notifyMediaAccessError } from '../media-errors';
import { CameraPreview } from './camera-preview';
import { DeviceMenu } from './device-menu';
import { MediaToggle } from './media-toggle';
import { ProductLogo } from './product-logo';
import { LanguageSwitcher } from '@/shared/language-switcher';
import { UserMenu } from '@/shared/user-menu';

export type PreJoinChoices = LocalUserChoices & { audioOutputDeviceId: string };

const AUDIO_OUTPUT_SELECTABLE = 'setSinkId' in HTMLMediaElement.prototype;

type PreJoinProps = {
    roomName: string;
    sessionName: string | undefined;
    onSubmit: (choices: PreJoinChoices) => Promise<void>;
};

export function PreJoin({ roomName, sessionName, onSubmit }: Readonly<PreJoinProps>) {
    const { t } = useTranslation('prejoin');
    const {
        userChoices,
        saveAudioInputEnabled,
        saveVideoInputEnabled,
        saveAudioInputDeviceId,
        saveVideoInputDeviceId,
        saveUsername,
    } = usePersistentUserChoices({ defaults: { audioEnabled: true, videoEnabled: true } });
    const [audioOutputDeviceId, setAudioOutputDeviceId] = useLocalStorage({ key: 'meet-audio-output', defaultValue: '' });
    const [submitting, setSubmitting] = useState(false);
    const [mediaPermitted, setMediaPermitted] = useState(false);
    const [nameMissing, setNameMissing] = useState(false);
    const submittingRef = useRef(false);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const joinButtonRef = useRef<HTMLButtonElement>(null);

    const { audioEnabled, videoEnabled, audioDeviceId, videoDeviceId } = userChoices;
    const username = sessionName ?? userChoices.username;
    const hasName = username.trim() !== '';
    const [focusNameOnOpen] = useState(!hasName);

    useEffect(() => {
        (focusNameOnOpen ? nameInputRef : joinButtonRef).current?.focus();
    }, [focusNameOnOpen]);

    const audioTracks = usePreviewTracks(
        { audio: audioEnabled && { deviceId: audioDeviceId || undefined } },
        notifyMediaAccessError
    );
    const videoTracks = usePreviewTracks(
        { video: videoEnabled && { deviceId: videoDeviceId || undefined } },
        notifyMediaAccessError
    );
    const audioTrack = audioEnabled ? (audioTracks?.[0] as LocalAudioTrack | undefined) : undefined;
    const videoTrack = videoEnabled ? (videoTracks?.[0] as LocalVideoTrack | undefined) : undefined;
    if (!mediaPermitted && (audioTrack || videoTrack)) setMediaPermitted(true);

    const handleSubmit = (event: SubmitEvent) => {
        event.preventDefault();
        if (submittingRef.current) return;
        if (!hasName) {
            setNameMissing(true);
            return;
        }
        submittingRef.current = true;
        setSubmitting(true);
        void onSubmit({ ...userChoices, username: username.trim(), audioOutputDeviceId }).finally(() => {
            submittingRef.current = false;
            setSubmitting(false);
        });
    };

    useHotkeys(
        [
            [
                'mod+D',
                (event) => {
                    if (!event.repeat) saveAudioInputEnabled(!audioEnabled);
                },
                { usePhysicalKeys: true },
            ],
            [
                'mod+E',
                (event) => {
                    if (!event.repeat) saveVideoInputEnabled(!videoEnabled);
                },
                { usePhysicalKeys: true },
            ],
        ],
        []
    );
    useHotkeys(
        [
            [
                'Enter',
                (event) => {
                    if (!hasName || document.querySelector('[role="menu"]')) return;
                    event.preventDefault();
                    joinButtonRef.current?.form?.requestSubmit();
                },
                { preventDefault: false },
            ],
        ],
        ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A']
    );

    return (
        <Box mih="100%" pos="relative" style={{ backgroundColor: 'var(--color-background-secondary)' }}>
            <Group justify="space-between" left={0} pos="absolute" pr="var(--spacing-4)" py="var(--spacing-2)" right={0} top={0}>
                <ProductLogo />
                <Group gap="var(--spacing-2)" wrap="nowrap">
                    <LanguageSwitcher />
                    <UserMenu />
                </Group>
            </Group>
            <Stack
                align="center"
                component="form"
                gap="var(--spacing-6)"
                justify="center"
                mih="100vh"
                mx="auto"
                px="var(--spacing-4)"
                py="var(--spacing-20)"
                w="min(100%, calc(var(--spacing-96) + var(--spacing-96) + var(--spacing-8)))"
                onSubmit={handleSubmit}
            >
                <Stack align="center" gap="var(--spacing-2)" ta="center">
                    <Title order={2}>{t('title')}</Title>
                    <Text c="dimmed">{roomName}</Text>
                </Stack>
                <Stack align="center" gap="var(--spacing-4)" w="100%">
                    <CameraPreview
                        name={username}
                        track={videoTrack}
                        controls={
                            <>
                                <MediaToggle
                                    disabledIcon={<IconMicrophoneOff size={24} />}
                                    enabled={audioEnabled}
                                    enabledIcon={<IconMicrophone size={24} />}
                                    shortcutKey="D"
                                    turnOffLabel={t('controls.microphoneOff')}
                                    turnOnLabel={t('controls.microphoneOn')}
                                    onToggle={() => {
                                        saveAudioInputEnabled(!audioEnabled);
                                    }}
                                />
                                <MediaToggle
                                    disabledIcon={<IconVideoOff size={24} />}
                                    enabled={videoEnabled}
                                    enabledIcon={<IconVideo size={24} />}
                                    shortcutKey="E"
                                    turnOffLabel={t('controls.cameraOff')}
                                    turnOnLabel={t('controls.cameraOn')}
                                    onToggle={() => {
                                        saveVideoInputEnabled(!videoEnabled);
                                    }}
                                />
                            </>
                        }
                    />
                    <Group key={String(mediaPermitted)} gap="var(--spacing-3)" justify="center">
                        <DeviceMenu
                            activeDeviceId={audioDeviceId || audioTrack?.mediaStreamTrack.getSettings().deviceId}
                            icon={<IconMicrophone size={20} />}
                            kind="audioinput"
                            label={t('devices.microphone')}
                            onSelect={saveAudioInputDeviceId}
                        />
                        {AUDIO_OUTPUT_SELECTABLE && (
                            <DeviceMenu
                                activeDeviceId={audioOutputDeviceId || 'default'}
                                icon={<IconVolume2 size={20} />}
                                kind="audiooutput"
                                label={t('devices.speaker')}
                                onSelect={setAudioOutputDeviceId}
                            />
                        )}
                        <DeviceMenu
                            activeDeviceId={videoDeviceId || videoTrack?.mediaStreamTrack.getSettings().deviceId}
                            icon={<IconVideo size={20} />}
                            kind="videoinput"
                            label={t('devices.camera')}
                            onSelect={saveVideoInputDeviceId}
                        />
                    </Group>
                </Stack>
                {sessionName === undefined && (
                    <TextInput
                        ref={nameInputRef}
                        autoComplete="name"
                        error={nameMissing && !userChoices.username.trim() ? t('name.required') : undefined}
                        label={t('name.label')}
                        placeholder={t('name.placeholder')}
                        radius="var(--radius-rounded-full)"
                        value={userChoices.username}
                        w="min(100%, var(--spacing-96))"
                        onChange={(event) => {
                            saveUsername(event.currentTarget.value);
                        }}
                    />
                )}
                <Button ref={joinButtonRef} loading={submitting} radius="var(--radius-rounded-full)" size="lg" type="submit">
                    {t('join')}
                </Button>
            </Stack>
        </Box>
    );
}
